<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { CdrText } from '@rei/cedar';

import useCardScroller from '../composables/useCardScroller';
import { useAnalytics } from '../../../common/composables/useAnalytics';
import { useTags } from '../../../common/composables/useTags';
import type { TagsType } from '../../../common/models/types/TagsType';

/**
 * Generic carousel for the "Help for you" section on the Help Center home.
 *
 * This component owns scrolling, arrows, and layout — nothing about what a
 * card looks like. Each feature (returns, member welcome, rewards, ...) is
 * responsible for its own card: its own anatomy, its own width, its own
 * styles. That is deliberate — the cards this holds today (return cards,
 * member welcome) share no internal structure, so there is nothing honest to
 * standardize beyond "renders inside a scrollable row."
 *
 * Callers pass finished content via the default slot, one direct child per
 * card. This component does not know or care what each child renders.
 */
const props = withDefaults(defineProps<{
  /** How many direct children are in the default slot, so arrows and the
   *  section itself can hide when there is nothing to show. Callers already
   *  know this count — it's cheaper than reflecting on slot content. */
  cardCount: number;
  title?: string;
  pageName?: string;
  /** Tag fired once when the section renders with at least one card. */
  sectionViewTag?: TagsType;
}>(), {
  title: 'Help for you',
  pageName: undefined,
  sectionViewTag: undefined,
});

const {
  trackRef,
  canScrollBack,
  canScrollForward,
  hasOverflow,
  scrollBack,
  scrollForward,
  syncScrollState,
} = useCardScroller();

const hasCards = computed(() => props.cardCount > 0);

onMounted(() => {
  if (!hasCards.value || !props.sectionViewTag) return;
  useAnalytics('sendViewAnalytics', {
    pageName: useTags('getTag', props.sectionViewTag),
  });
});
</script>

<template>
  <section
    v-if="hasCards"
    class="help-cards"
    data-ui="help-cards-section"
  >
    <div class="help-cards__inner">
      <CdrText
        tag="h2"
        class="help-cards__title"
      >
        {{ title }}
      </CdrText>

      <div class="help-cards__viewport">
        <!-- tabindex makes the track reachable by keyboard, which is what
             gives arrow-key scrolling for free on an overflow container -->
        <div
          ref="trackRef"
          class="help-cards__track"
          role="group"
          :aria-label="title"
          tabindex="0"
          data-ui="help-cards-track"
          @scroll.passive="syncScrollState"
        >
          <slot />
        </div>

        <button
          v-if="hasOverflow && canScrollBack"
          type="button"
          class="help-cards__control help-cards__control--prev"
          aria-label="Scroll to previous card"
          data-ui="help-cards-scroll-back"
          @click="scrollBack"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          ><path d="M15 18l-6-6 6-6" /></svg>
        </button>
        <button
          v-if="hasOverflow && canScrollForward"
          type="button"
          class="help-cards__control help-cards__control--next"
          aria-label="Scroll to next card"
          data-ui="help-cards-scroll-forward"
          @click="scrollForward"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          ><path d="M9 18l6-6-6-6" /></svg>
        </button>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
  /* Shared card anatomy is imported here, once, rather than from each card
     file — every card in this carousel uses it. */
  @import 'src/main/style/helpCard.scss';
  @import 'src/main/style/helpCardsCarousel.scss';
</style>
